<template>
<div class="form-step-item step-4">
    <div>
        <div class="formulation-form-row">
            <div class="col-8">
                <div class="panel top-info">
                    <h3>Formulation Submitted</h3>
                    <div class="formulation-form-row">
                        <div class="col-12">
                            <label>Formulation Detailed Description</label>
                            <p>{{formData.bag_details}}</p>
                        </div>
                    </div>
                    <template v-if="formData.use==='patient'">
                        <h4>Patient Details</h4>
                        <div class="formulation-form-row">
                            <div class="col-4">
                                <label>Initials</label>
                                <p>{{formData.patient_initials}}</p>
                            </div>
                            <div class="col-8">
                                <label>Hospital or HPN Formulation Reference</label>
                                <p>{{formData.patient_ref}}</p>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
            <div class="col-4 print-full">
                <SavedPanel :formData="formData" />
                <div class="action-icons no-print">
                    <div class="list">
                        <button><i class="icon-printer" @click.prevent="onPrint()"></i></button>
                        <button><i class="icon-copy" @click.prevent="onModalCopyFormulationOpen()"></i></button>
                    </div>
                </div>
            </div>
        </div>
        <div class="formulation-form-row">
            <div class="col-8 print-full">
                <div class="create-formulation-wrapper">
                    <div class="inner">
                        <div class="section-heading">
                            <div class="formulation-form-row">
                                <div class="col-7 d-flex align-items-center">
                                    <label for="bag_volume">Bag Volume</label>
                                    <template v-if="baseInfo.bag_type === 'triple_chamber'">
                                        <span class="form-bag-volume">{{baseInfo.bag_volume}} mL</span>
                                    </template>
                                    <template v-else>
                                        <input type="number" disabled="disabled" class="form-control" v-model="formData.bag_volume">
                                        <span class="form-uom">mL</span>
                                    </template>
                                </div>
                                <div class="col-5 d-flex align-items-center">
                                    <h4>Base: {{baseInfo.label}}</h4>
                                </div>
                            </div>
                            <div class="formulation-form-row" v-if="formData.errors.bag">
                                <div class="col-7">
                                    <span class="form-error">{{formData.errors.bag}} Please <router-link :to="{ name: 'TPNCustomFormulationContact' }">contact us</router-link> if you would like to discuss options.</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Macronutrients -->
                    <div class="inner" v-if="baseInfo.macronutrients">
                        <div class="section-macronutrients">
                            <h4>Macronutrients</h4>
                            <!-- Amino acid -->
                            <div class="subpanel" v-if="baseInfo.macronutrients.amino_acid">
                                <div class="formulation-form-row align-items-center">
                                    <div class="col-4">
                                        <h5 class="label">Amino acid</h5>
                                    </div>
                                    <div v-if="baseInfo.label === 'Primene'" class="col-8">
                                        <h5 class="label">Total per litre</h5>
                                    </div>
                                </div>
                                <div class="formulation-form-row align-items-center">
                                    <div class="col-4">
                                        <select disabled="disabled" class="form-control" v-if="baseInfo.macronutrients.amino_acid.choices.length > 1" v-model="formData.formulation.macronutrients.amino_acid.label">
                                            <option class="d-none" value="">- Select -</option>
                                            <option value="none">Not Required</option>
                                            <option v-for="(type, typeIndex) in baseInfo.macronutrients.amino_acid.choices" :value="type" v-bind:key="typeIndex">
                                                {{type}}
                                            </option>
                                        </select>
                                        <p v-if="baseInfo.macronutrients.amino_acid.choices.length === 1">{{formData.selectedAminoAcidType.label}}</p>
                                    </div>
                                    <div class="col-3" v-if="formData.selectedAminoAcidType">
                                        <input disabled="disabled" type="number" class="form-control" v-model="formData.formulation.macronutrients.amino_acid.quantity" >
                                        <span class="form-uom">g</span>
                                    </div>
                                    <div class="col-5" v-if="formData.formulation.macronutrients.amino_acid.nitrogen">
                                        <p>Nitrogen {{formatNumber(formData.formulation.macronutrients.amino_acid.nitrogen, 2) }} {{formData.selectedAminoAcidType.nitrogen_unit}}</p>
                                    </div>
                                </div>
                                <div class="formulation-form-row" v-if="formData.errors.aminoacid">
                                    <div class="col-4"></div>
                                    <div class="col-8 error-col">
                                        <span class="form-error">Outside guidelines for stability</span>
                                    </div>
                                </div>
                            </div>
                            <!-- Glucose -->
                            <div class="subpanel" v-if="baseInfo.macronutrients.glucose">
                                <div class="formulation-form-row align-items-center">
                                    <div class="col-4">
                                        <h5 class="label">Glucose</h5>
                                    </div>
                                    <div v-if="baseInfo.label === 'Primene'" class="col-8">
                                        <h5 class="label">Total per litre</h5>
                                    </div>
                                </div>
                                <div class="formulation-form-row">
                                    <div class="col-4">
                                        <select disabled="disabled" class="form-control" v-if="baseInfo.macronutrients.glucose.choices.length > 1" v-model="formData.formulation.macronutrients.glucose.label" >
                                            <option class="d-none" value="">- Select -</option>
                                            <option value="none">Not Required</option>
                                            <option v-for="(type, typeIndex) in baseInfo.macronutrients.glucose.choices" :value="type" v-bind:key="typeIndex">
                                                {{type}}
                                            </option>
                                        </select>
                                        <p v-if="baseInfo.macronutrients.glucose.choices.length === 1">{{formData.selectedGlucoseType.label}}</p>
                                    </div>
                                    <div class="col-3" v-if="formData.selectedGlucoseType">
                                        <div>
                                            <input disabled="disabled" type="number" class="form-control" v-model="formData.formulation.macronutrients.glucose.quantity_g">
                                            <span class="form-uom">g</span>
                                        </div>
                                        <div>
                                            <input disabled="disabled" type="number" class="form-control" v-model="formData.formulation.macronutrients.glucose.quantity_kcal" >
                                            <span class="form-uom">kcal</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="formulation-form-row" v-if="formData.errors.glucose">
                                    <div class="col-4"></div>
                                    <div class="col-8 error-col">
                                        <span class="form-error">Outside guidelines for stability</span>
                                    </div>
                                </div>
                            </div>
                            <!-- Lipid -->
                            <div class="subpanel" v-if="baseInfo.macronutrients.lipid">
                                <div class="formulation-form-row align-items-center">
                                    <div class="col-4">
                                        <h5 class="label">Lipid</h5>
                                    </div>
                                    <div v-if="baseInfo.label === 'Primene'" class="col-8">
                                        <h5 class="label">Total per litre</h5>
                                    </div>
                                </div>
                                <div class="formulation-form-row">
                                    <div class="col-4">
                                        <select disabled="disabled" class="form-control" v-if="baseInfo.macronutrients.lipid.choices.length > 1" v-model="formData.formulation.macronutrients.lipid.label">
                                            <option class="d-none" value="">- Select -</option>
                                            <option v-for="(type, typeIndex) in baseInfo.macronutrients.lipid.choices" :value="type" v-bind:key="typeIndex">
                                                {{type}}
                                            </option>
                                        </select>
                                        <p v-if="baseInfo.macronutrients.lipid.choices.length === 1">{{formData.selectedLipidType.label}}</p>
                                    </div>
                                    <div class="col-3" v-if="formData.selectedLipidType">
                                        <div>
                                            <input disabled="disabled" type="number" class="form-control" v-model="formData.formulation.macronutrients.lipid.quantity_g">
                                            <span class="form-uom">g</span>
                                        </div>
                                        <div>
                                            <input disabled="disabled" type="number" class="form-control" v-model="formData.formulation.macronutrients.lipid.quantity_kcal">
                                            <span class="form-uom">kcal</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="formulation-form-row" v-if="formData.errors.lipids.default">
                                    <div class="col-4"></div>
                                    <div class="col-8 error-col">
                                        <span class="form-error">{{formData.errors.lipids.default}}</span>
                                    </div>
                                </div>
                                <div class="formulation-form-row" v-if="formData.errors.lipids.noVitalipid">
                                    <div class="col-12 error-col">
                                        <span class="form-error general">If you would like to add Omegaven to this formulation, please <router-link :to="{ name: 'TPNCustomFormulationContact' }">contact us</router-link></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="inner" v-if="baseInfo.electrolytes.items">
                        <div class="section-electrolytes">
                            <!-- Electrolytes list -->
                            <div class="subpanel">
                                <div class="formulation-form-row">
                                    <div class="col-4 d-flex align-items-center">
                                        <h4>Electrolytes</h4>
                                    </div>

                                    <template v-if="baseInfo.bag_type === 'triple_chamber'">
                                        <div class="col-3 d-flex align-items-center">
                                            <p>Base Quantity</p>
                                        </div>
                                    </template>
                                    <template v-else>
                                        <div class="col-1 d-flex align-items-center">
                                        </div>
                                    </template>

                                    <template v-if="baseInfo.bag_type === 'triple_chamber'">
                                        <div class="col-5 d-flex align-items-center">
                                            <p>Extra to be added</p>
                                        </div>
                                    </template>
                                    <template v-else>
                                        <div class="col-3 d-flex align-items-center">
                                            <p v-if="baseInfo.label === 'Primene'">Total per litre</p>
                                            <p v-else>Add to bag</p>
                                        </div>
                                    </template>

                                </div>
                                <template v-for="(item, ItemIndex) in baseInfo.electrolytes.items">
                                    <div class="formulation-form-row" :class="{ 'mt-2': ItemIndex !== 0 }" :key="'data_' + ItemIndex" >
                                        <template v-if="item.choices">
                                            <template v-if="item.choices.length > 1">
                                                <div class="col-12">
                                                    <p>{{item.label}}</p>
                                                </div>
                                                <div class="col-4" :class="{ 'd-flex align-items-center': baseInfo.label !== 'Primene' }">
                                                    <select class="form-control" disabled="disabled" v-model="formData.formulation.electrolytes[ItemIndex].type">
                                                        <option class="d-none" value="">- Select -</option>
                                                        <!-- <option value="none">Not Required</option> -->
                                                        <option v-for="(type, typeIndex) in item.choices" :value="type.label" v-bind:key="typeIndex">
                                                            {{type.label}}
                                                        </option>
                                                    </select>
                                                </div>
                                            </template>
                                            <template v-else>
                                                <div class="col-4 d-flex align-items-center">
                                                    <p v-if="baseInfo.label === 'Synthamin + Lipid'">{{item.label}}<br>({{item.choices[0].label}}, excl. lipid)</p>
                                                    <p v-else>{{item.label}} ({{item.choices[0].label}})</p>
                                                </div>
                                            </template>
                                        </template>
                                        <template v-else>
                                            <div class="col-4 d-flex align-items-center">
                                                <template v-if="baseInfo.label === 'Primene' && item.label === 'Sodium' && hasOrganicPhosphate()">
                                                    <p>{{item.label}} (excl. Phosphate)</p>
                                                </template>
                                                <template v-else>
                                                <p>{{item.label}}</p>
                                                </template>
                                            </div>
                                        </template>
                                        <template v-if="baseInfo.bag_type === 'triple_chamber'">
                                            <div class="col-3 d-flex align-items-center">
                                                <p>{{getBaseQuantity(item, ItemIndex)}}</p>
                                            </div>
                                        </template>
                                        <template v-else>
                                            <div class="col-1 d-flex align-items-center">
                                            </div>
                                        </template>
                                        <div :class="{ 'col-5': baseInfo.bag_type === 'triple_chamber', 'col-3': baseInfo.bag_type !== 'triple_chamber','d-flex align-items-center': baseInfo.label !== 'Primene'  }">
                                            <div class="d-flex align-items-center" v-if="!item.choices || formData.formulation.electrolytes[ItemIndex].type !== ''">
                                                <input disabled="disabled" type="number" class="form-control" v-model="formData.formulation.electrolytes[ItemIndex].extra_quantity" >
                                                <span class="form-uom">{{item.unit}}</span>
                                            </div>
                                        </div>
                                        <template v-if="baseInfo.label === 'Primene' && item.label === 'Phosphate' && hasOrganicPhosphate()">
                                            <div class="col-4" >
                                                <div v-html="getPrimenePhosphateMessage()" class="extra-message"></div>
                                            </div>
                                        </template>
                                    </div>
                                    <div class="formulation-form-row" v-if="formData.errors.electrolytes['item_' + ItemIndex]" :key="'validation_' + ItemIndex" >
                                        <template v-if="baseInfo.bag_type === 'triple_chamber'">
                                            <div class="col-7"></div>
                                            <div class="col-5 error-col">
                                                <span class="form-error">Outside guidelines for stability</span>
                                            </div>
                                        </template>
                                        <template v-else>
                                            <div class="col-5"></div>
                                            <div class="col-5 error-col">
                                                <span class="form-error">Outside guidelines for stability</span>
                                            </div>
                                        </template>
                                    </div>
                                </template>
                            </div>
                            <!-- Chloride + Acetate -->
                            <div class="subpanel" v-if="baseInfo.electrolytes.chloride_acetate">
                                <div class="formulation-form-row align-items-center">
                                    <div class="col-4">
                                        <h5 class="label">Chloride + Acetate</h5>
                                    </div>
                                    <div v-if="baseInfo.label === 'Primene' && (formData.formulation.chloride_acetate.label === 'Specified Chloride' || formData.formulation.chloride_acetate.label === 'Specified Acetate')" class="col-8">
                                        <h5 class="label">Total per litre</h5>
                                    </div>
                                </div>
                                <div class="formulation-form-row">
                                    <div class="col-4 d-flex align-items-center">
                                        <select class="form-control" disabled="disabled" v-model="formData.formulation.chloride_acetate.label">
                                            <option class="d-none" value="">- Select -</option>
                                            <option v-for="(type, typeIndex) in formulationsData.settings.chloride_acetate.choices" :value="type" v-bind:key="typeIndex">
                                                {{type}}
                                            </option>
                                        </select>
                                    </div>
                                    <div class="col-3" v-if="formData.formulation.chloride_acetate.label === 'Specified Chloride' || formData.formulation.chloride_acetate.label === 'Specified Acetate'">
                                        <input disabled="disabled" type="number" class="form-control" v-model="formData.formulation.chloride_acetate.quantity">
                                        <span class="form-uom">mmol</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Micronutrients -->
                    <div class="inner" v-if="baseInfo.micronutrients">
                        <div class="section-micronutrients">
                            <!-- Micronutrients unit switcher -->
                            <div class="intro">
                                <div class="formulation-form-row align-items-center">
                                    <div class="col-7">
                                        <h4>Micronutrients</h4>
                                    </div>
                                </div>
                            </div>
                            <!-- Trace Elements -->
                            <div class="subpanel" v-if="baseInfo.micronutrients.trace_elements">
                                <div class="formulation-form-row align-items-center">
                                    <div class="col-4">
                                        <h5 class="label">Trace Element</h5>
                                    </div>
                                    <div v-if="baseInfo.label === 'Primene' && formData.formulation.micronutrients.trace_elements[0].name && formData.formulation.micronutrients.trace_elements[0].name !== 'none'" class="col-8">
                                        <h5 class="label">Total per litre</h5>
                                    </div>
                                </div>
                                <div class="micronutrients-list type-te">
                                    <div class="micronutrients-item" v-for="(item, index) in formData.formulation.micronutrients.trace_elements" :key="index" >
                                        <MicronutrientTraceElementRow :rowIndex="index" :rowItem="item" :elementsList="baseInfo.micronutrients.trace_elements" :formTraceElements="formData.formulation.micronutrients.trace_elements" :micronutrientsUnit="formData.micronutrientsUnit" :country="formData.country" :settings="formulationsData.settings.trace_elements" :viewMode="true"  :error="formData.errors.trace_elements[index]" :errorMulti="formData.errors.trace_elements_multi[index]" :ref="'te_row_' + index"></MicronutrientTraceElementRow>
                                    </div>
                                </div>
                            </div>
                            <!-- Vitamins -->
                            <div class="subpanel" v-if="baseInfo.micronutrients.vitamins">
                                <div class="formulation-form-row align-items-center">
                                    <div class="col-4">
                                        <h5 class="label">Vitamins</h5>
                                    </div>
                                    <div v-if="baseInfo.label === 'Primene' && formData.formulation.micronutrients.vitamins[0].name && formData.formulation.micronutrients.vitamins[0].name !== 'none'" class="col-8">
                                        <h5 class="label">Total per litre</h5>
                                    </div>
                                </div>
                                <div class="micronutrients-list type-vitamin">
                                    <div class="micronutrients-item" v-for="(item, index) in formData.formulation.micronutrients.vitamins" :key="index" >
                                        <MicronutrientVitaminRow :rowIndex="index" :rowItem="item" :elementsList="baseInfo.micronutrients.vitamins" :formVitamins="formData.formulation.micronutrients.vitamins" :bagName="baseInfo.label" :viewMode="true" :country="formData.country" :error="formData.errors.vitamins[index]" :selectedLipid="formData.formulation.macronutrients.lipid.label" :ref="'vitamin_row_' + index"></MicronutrientVitaminRow>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Additives -->
                    <div class="inner" v-if="baseInfo.additives">
                        <div class="section-additives">
                            <div class="formulation-form-row align-items-center">
                                <div class="col-4">
                                    <h4>Additives</h4>
                                </div>
                                <div v-if="baseInfo.label === 'Primene'" class="col-8">
                                    <h5 class="label">Total per litre</h5>
                                </div>
                            </div>
                            <template v-for="(item, ItemIndex) in baseInfo.additives.items">
                                <div class="formulation-form-row" :key="ItemIndex">
                                    <div class="col-4 d-flex align-items-center">
                                        <p>{{item.label}}</p>
                                    </div>
                                    <div class="col-3 d-flex align-items-center">
                                        <input disabled="disabled" type="number" class="form-control" v-model="formData.formulation.additives[ItemIndex].quantity">
                                        <span class="form-uom">{{item.unit}}</span>
                                    </div>
                                </div>
                                <div class="formulation-form-row" v-if="formData.errors.additives['item_' + ItemIndex]" :key="'validation_additives_' + ItemIndex" >
                                    <div class="col-4"></div>
                                    <div class="col-8 error-col">
                                        <span class="form-error">Outside guidelines for stability</span>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </div>
                    <!-- comments -->
                    <div class="inner">
                        <div class="section-comments">
                            <h4>Comments</h4>
                            <div class="formulation-form-row">
                                <div class="col-9 d-flex align-items-center">
                                    <textarea  disabled="disabled" class="form-control" v-model="formData.comments"></textarea>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="link-back">
                    <router-link :to="{ name: 'TPNCustomFormulation' }" >&lt; Back to Custom Formulations</router-link>
                </div>
            </div>
            <div class="col-4 print-full">
                <RecapTable :formData="formData" :baseInfo="baseInfo" :settings="formulationsData.settings" :showComments="false" :ref="'recap_table'" />
                <StabilityPanel :stability="formData.stability" />
            </div>
        </div>
        <CopyFormulationModal class="copy-formulation-modal" :open="modalCopyFormulation" @modal_copy_formulation="onModalCopyFormulation" />
    </div>
</div>
</template>
<script>

import tpnUtilsMixin from '@/mixins/tpnUtilsMixin';
import MicronutrientTraceElementRow from '@/views/Tpn/CustomFormulationCreate/MicronutrientTraceElementRow.vue';
import MicronutrientVitaminRow from '@/views/Tpn/CustomFormulationCreate/MicronutrientVitaminRow.vue';
import RecapTable from '@/views/Tpn/CustomFormulationCreate/RecapTable.vue';
import StabilityPanel from '@/views/Tpn/CustomFormulationCreate/StabilityPanel.vue';
import SavedPanel from '@/views/Tpn/CustomFormulationCreate/SavedPanel.vue';
import CopyFormulationModal from '@/views/Tpn/CustomFormulationCreate/CopyFormulationModal.vue';

export default {
    name: 'FormStep4',
    mixins: [tpnUtilsMixin],
    components: {
        MicronutrientTraceElementRow,
        MicronutrientVitaminRow,
        RecapTable,
        StabilityPanel,
        SavedPanel,
        CopyFormulationModal
    },
    props: {
        formData: {
            required: true,
        },
        formulationsData: {
            required: true,
        },
        viewMode: {
            required: true,
        },
    },
    data() {
        return {
            baseInfo: this.formulationsData.bases[this.formData.base],
            modalCopyFormulation: false,
        };
    },
    validations: {
    },
    computed: {
    },
    methods: {
        getPrimenePhosphateMessage() {
            return "<p>Organic Phosphate will provide extra Sodium as 2mmol sodium per mmol Organic Phosphate.<br><strong>Please check total Sodium required carefully</strong></p>";
        },
        hasOrganicPhosphate() {
            let found = false;
            this.formData.formulation.electrolytes.forEach((item) => {
                if (item.label === 'Phosphate' && item.type === 'Organic' && item.extra_quantity) {
                    found = true;
                }
            });
            return found;
        },
        getBaseQuantity(item, ItemIndex) {
            let value = 0;
            if (item.choices) {
                if (item.choices.length > 1) {
                    const selectedItem = item.choices.find((el) => el.label === this.formData.formulation.electrolytes[ItemIndex].type);
                    if (selectedItem) {
                        value = selectedItem.base_quantity;
                    }
                } else {
                    value = item.choices[0].base_quantity;
                }
            } else {
                value = item.base_quantity;
            }
            return this.formatNumber(value, 2) + ' ' + item.unit;
        },
        onPrint() {
            window.print();
        },
        onModalCopyFormulationOpen() {
            this.modalCopyFormulation = true;
        },
        onModalCopyFormulation(copy) {
            if (copy) {
                this.savedId = false;
                this.$emit('set_preloaded_data', this.formData);
                const path = '/tpn/customFormulationCreate';
                if (this.$route.path !== path) {
                    this.$router.push({ name: 'TPNCustomFormulationCreate' });
                }
            }
            this.modalCopyFormulation = false;
        },
    },
};
</script>
